import { useState, useCallback, useMemo } from 'react';
import { FormStep, FormStepConfig } from '../types';
import type { FieldErrors } from '../utils/formValidation';

export interface UseFormNavigationReturn {
  currentStep: FormStep;
  currentStepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  completedSteps: FormStep[];
  stepErrors: FieldErrors;
  goToStep: (step: FormStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  clearStepErrors: () => void;
}

/**
 * Hook para gerenciar a navegação entre etapas do formulário.
 * Aceita um validador opcional que bloqueia avanço quando retorna erros.
 */
export function useFormNavigation(
  steps: FormStepConfig[],
  validateCurrentStep?: (step: FormStep) => FieldErrors,
): UseFormNavigationReturn {
  const [currentStep, setCurrentStep] = useState<FormStep>('identification');
  const [completedSteps, setCompletedSteps] = useState<FormStep[]>([]);
  const [stepErrors, setStepErrors] = useState<FieldErrors>({});

  const currentStepIndex = useMemo(
    () => steps.findIndex((s) => s.id === currentStep),
    [steps, currentStep]
  );

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const goToStep = useCallback((step: FormStep) => {
    setStepErrors({});
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    // Validar step atual antes de avançar
    if (validateCurrentStep) {
      const errors = validateCurrentStep(currentStep);
      if (Object.keys(errors).length > 0) {
        setStepErrors(errors);
        return;
      }
    }

    setStepErrors({});
    const idx = steps.findIndex((s) => s.id === currentStep);
    const last = idx === steps.length - 1;
    if (!last) {
      const nextStep = steps[idx + 1];
      setCompletedSteps((prev) => {
        if (!prev.includes(currentStep)) {
          return [...prev, currentStep];
        }
        return prev;
      });
      setCurrentStep(nextStep.id);
    }
  }, [currentStep, steps, validateCurrentStep]);

  const goToPreviousStep = useCallback(() => {
    setStepErrors({});
    const idx = steps.findIndex((s) => s.id === currentStep);
    const first = idx === 0;
    if (!first) {
      const prevStep = steps[idx - 1];
      setCurrentStep(prevStep.id);
    }
  }, [currentStep, steps]);

  const clearStepErrors = useCallback(() => {
    setStepErrors({});
  }, []);

  return {
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    completedSteps,
    stepErrors,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    clearStepErrors,
  };
}
